// Shared AI-mode variable model. Three kinds of on-screen runtime variable used
// by the flowchart in AI mode:
//   counter (Bicounter1/2) — a plain tally, only +1 / -1;
//   data    (Bidata1/2)    — a free number you can assign/add/subtract for coding;
//   flag    (Biflag1/2)    — On/Off.
// Kept in one place so the Start / Action / Decision panels and the Execute
// runtime agree on ids, kinds and operations.

// The three kinds of AI variable, each with how many the user may add (a cap so
// a project can't spawn unlimited variables). Ids keep the legacy "Bi" prefix
// (Bic/Bid/Bif) so saved projects still resolve; display names drop it.
export const VAR_TYPES = [
    { kind: 'counter', label: 'Counter', prefix: 'Bic', name: 'Counter', max: 8 },
    { kind: 'data', label: 'Data', prefix: 'Bid', name: 'Data', max: 8 },
    { kind: 'flag', label: 'Flag', prefix: 'Bif', name: 'Flag', max: 8 },
]

// Full universe of possible variables (all caps of every type). Only the ones
// the user turns on in Start are actually "enabled".
export const AI_VARIABLES = VAR_TYPES.flatMap((t) =>
    Array.from({ length: t.max }, (_, i) => ({
        id: `${t.prefix}${i + 1}`,
        name: `${t.name}${i + 1}`,
        kind: t.kind,
    }))
)

// The kind an id belongs to, from its prefix (Bic/Bid/Bif).
export const kindOfId = (id) =>
    (VAR_TYPES.find((t) => typeof id === 'string' && id.startsWith(t.prefix)) ||
        {}).kind || null

// Start-block items shown in AI mode: only the counters, data and flags. BT
// Remote and the TX/RX serial items are hidden here (BT Remote can return later).
export const AI_START_ITEMS = AI_VARIABLES.map((v) => ({
    name: v.name,
    id: v.id,
    isEnabled: false,
}))

export const varById = (id) => AI_VARIABLES.find((v) => v.id === id) || null

// The counters/flags explicitly enabled in the Start block. Strict: a counter/
// flag only becomes available (in the Action and Decision panels) once the user
// turns it on in Start — nothing is shown by default.
export function loadEnabledVars() {
    try {
        const raw = sessionStorage.getItem('startButtonState')
        if (!raw) return []
        const state = JSON.parse(raw)
        return AI_VARIABLES.filter((v) => {
            const item = state.find((s) => s.id === v.id)
            return item && item.isEnabled
        })
    } catch {
        return []
    }
}

// Operations a variable action can apply.
//   counter — a tally, so only step by one; no operand.
//   data    — arithmetic on a free number (assign / add / subtract any value).
//   flag    — set (On/Off) or toggle.
export const COUNTER_OPS = [
    { value: 'inc', label: 'Count +1' },
    { value: 'dec', label: 'Count -1' },
]
export const DATA_OPS = [
    { value: 'set', label: '=' },
    { value: 'add', label: '+=' },
    { value: 'sub', label: '-=' },
]
export const FLAG_OPS = [
    { value: 'set', label: 'Set' },
    { value: 'toggle', label: 'Toggle' },
]
export const opsFor = (kind) =>
    kind === 'flag' ? FLAG_OPS : kind === 'counter' ? COUNTER_OPS : DATA_OPS

// An action's operand can be a fixed Value (typed) or Assigned from a live
// source. Assign sources are the object count (only offered when the Object
// feature is selected) and the other variables' live values.
export const ACTION_MODES = [
    { value: 'value', label: 'Value' },
    { value: 'assign', label: 'Assign' },
]

// Build the "Assign" dropdown sources for the current setup.
export function assignSources({ hasObjectCount, hasSpeech, vars }) {
    const list = []
    if (hasObjectCount) list.push({ value: 'count', label: 'Object count' })
    if (hasSpeech) list.push({ value: 'speech', label: 'Speech (live text)' })
    ;(vars || []).forEach((v) => list.push({ value: v.id, label: v.name }))
    return list
}

// Resolve an action's Assign reference to a live number.
// ctx = { count, vars } — the live object count and the variable store.
function resolveAssign(assignRef, ctx) {
    if (assignRef === 'count') return Number(ctx.count) || 0
    return Number((ctx.vars || {})[assignRef]) || 0
}

// Text-valued assign sources (returned as a string, not a number). Speech is
// the live transcript; everything else falls through to the numeric resolver.
function resolveAssignText(assignRef, ctx) {
    if (assignRef === 'speech') return String(ctx.transcript || '')
    if (assignRef === 'count') return String(Number(ctx.count) || 0)
    return String(Number((ctx.vars || {})[assignRef]) || 0)
}

// The numeric operand an action contributes this run (Value or Assign).
export function resolveOperand(action, ctx) {
    if (action.mode === 'assign')
        return resolveAssign(action.assignRef, ctx)
    return Number(action.value) || 0
}

// The text a display action shows: a typed literal, or the live value of an
// assigned source rendered as text.
export function resolveActionText(action, ctx) {
    if (action.mode === 'assign')
        return resolveAssignText(action.assignRef, ctx)
    return typeof action.text === 'string' ? action.text : ''
}

// Apply one variable action to a variable store, returning the new value.
// ctx = { count, vars } for resolving an assigned operand.
export function applyVarOp(current, action, ctx = {}) {
    const cur = Number(current) || 0
    const kind = varById(action.varId)?.kind || 'counter'
    if (kind === 'flag') {
        if (action.op === 'toggle') return cur ? 0 : 1
        return Number(action.value) ? 1 : 0
    }
    if (kind === 'counter') {
        // A counter only ever steps by one.
        return action.op === 'dec' ? cur - 1 : cur + 1
    }
    // data: assign / add / subtract a value or an assigned live source.
    const operand = resolveOperand(action, ctx)
    if (action.op === 'add') return cur + operand
    if (action.op === 'sub') return cur - operand
    return operand
}
